import { DesignModelDto } from 'src/api/FiretrolApiClient';
import {
  DesignModelFileKind,
  FILE_KINDS,
  joinFileLabels,
  missingFilesSentence,
  savedFilesOf,
} from 'src/routes/Jobs/DesignModels/designModelFiles';

export type DesignModelListStatus =
  | 'Ready'
  | 'Processing'
  | 'Failed'
  | 'Incomplete'
  | 'Not processed';

export const presentFileKinds = (
  model: Pick<DesignModelDto, 'attachment'>,
): DesignModelFileKind[] =>
  FILE_KINDS.filter(
    (kind) => savedFilesOf(model.attachment ?? [], kind).length > 0,
  );

/** Processing and Failed outrank Incomplete because they describe what the server is doing or last did. */
export const getDesignModelListStatus = (
  model: Pick<DesignModelDto, 'conversionStatus' | 'attachment'>,
): DesignModelListStatus => {
  if (model.conversionStatus === 'Processing') return 'Processing';
  if (model.conversionStatus === 'Failed') return 'Failed';
  if (presentFileKinds(model).length < FILE_KINDS.length) return 'Incomplete';
  if (model.conversionStatus === 'Ready') return 'Ready';
  return 'Not processed';
};

export const listStatusTooltip = (
  model: Pick<
    DesignModelDto,
    'conversionStatus' | 'conversionError' | 'attachment'
  >,
): string | null => {
  const status = getDesignModelListStatus(model);
  if (status === 'Failed') return model.conversionError || 'Processing failed.';
  if (status === 'Incomplete') {
    const present = presentFileKinds(model);
    const absent = FILE_KINDS.filter((k) => !present.includes(k));
    return `No ${joinFileLabels(absent, 'disjunction')} file`;
  }
  return null;
};

export const deleteDesignModelCopy = (name: string | undefined) => ({
  title: 'Delete Design Model',
  text: `Delete "${
    name ?? ''
  }"? This deletes the model, its 3D model and line grouping, all progress recorded against it, and the WBS accounts built from its FAB file. This cannot be undone.`,
});

/** A missing source already explains the failure, and Reprocess is blocked until it is replaced, so no Reprocess advice is added. */
export const failedStatusText = (
  conversionError: string | null | undefined,
  missing: DesignModelFileKind[],
): string =>
  missing.length > 0
    ? conversionError || missingFilesSentence(missing)
    : `${
        conversionError || 'Processing failed.'
      } Use Reprocess above to try again.`;
